import { FilterValues, UseGameFilterReturn } from "@/shared/types/filter";
import { useCallback, useMemo, useState } from "react";

/**
 * Хук для управления фильтрацией игр
 * @param initialFilters - начальные значения фильтров
 * @returns объект с методами и состоянием фильтрации
 */
export const useGameFilter = (
  initialFilters: FilterValues = {}
): UseGameFilterReturn => {
  const [filterValues, setFilterValues] =
    useState<FilterValues>(initialFilters);
  const [activeFilters, setActiveFilters] =
    useState<FilterValues>(initialFilters);
  const [searchQuery, setSearchQuery] = useState("");

  // Устанавливаем значение отдельного фильтра
  const setFilterValue = useCallback(
    (name: string, value: string | string[]) => {
      setFilterValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  // Применяем фильтры
  const applyFilters = useCallback(() => {
    setActiveFilters(filterValues);
  }, [filterValues]);

  // Сбрасываем все фильтры
  const resetFilters = useCallback(() => {
    setFilterValues(initialFilters);
    setActiveFilters(initialFilters);
    setSearchQuery("");
  }, [initialFilters]);

  // Проверяем наличие активных фильтров
  const hasActiveFilters = useMemo(() => {
    const hasFilters = Object.values(activeFilters).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== "";
    });
    return hasFilters || searchQuery.length > 0;
  }, [activeFilters, searchQuery]);

  return {
    filterValues,
    searchQuery,
    setSearchQuery,
    setFilterValue,
    applyFilters,
    resetFilters,
    activeFilters,
    hasActiveFilters,
  };
};
